// src/pages/ClubProjects.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import usePageTitle from "../hooks/usePageTitle";
import { Link } from "react-router-dom";
import {
  FaPlus,
  FaSearch,
  FaCheckCircle,
  FaHourglassHalf,
  FaRocket,
  FaExclamationTriangle,
  FaChevronDown,
  FaThumbsUp,
  FaClock,
  FaStar,
  FaTimesCircle,
  FaShieldAlt,
} from "react-icons/fa";

/* ------------------------------- Constants ------------------------------- */

const ABSOLUTE_HOST = "https://dev.sliitleo.org";

// Layout offsets
const SIDEBAR_W = 256; // px — match your desktop Sidebar width
const MAIN_GUTTER = 40; // px — extra space from sidebar on desktop
const TOPBAR_H = 56; // px — space for Topbar

// Brand palette — luxe navy
const C = {
  bg: "#0b1120",
  panel: "#0f1528",
  panel2: "#0e172a",
  tile: "#111b31",
  ink: "#eaf2ff",
  mute: "#cfe0ff",
  meta: "#9fb4d9",
  border: "rgba(255,255,255,.08)",

  blue: "#60a5fa",
  blueD: "#3b82f6",
  cyan: "#06b6d4",
  violet: "#8b5cf6",
  indigo: "#6366f1",
  gold: "#ffd54a",
  green: "#059669",
  greenSoft: "rgba(16,185,129,.16)",
  red: "#e11d48",
  redSoft: "rgba(225,29,72,.16)",
  skySoft: "rgba(2,132,199,.16)",

  shadowL: "0 20px 60px -30px rgba(0,0,0,.8)",
  shadowM: "0 14px 44px -26px rgba(0,0,0,.75)",
  shadowS: "0 8px 28px -18px rgba(0,0,0,.6)",
};

/* ------------------------------ Style System ----------------------------- */

const S = {
  font: { fontFamily: '"Inter","SF Pro Text",system-ui,-apple-system,Segoe UI,Roboto,sans-serif' },
  app: { display: "flex", background: C.bg, color: C.ink, minHeight: "100vh" },

  main: (isDesktop) => ({
    width: "100%",
    padding: "24px 24px 40px",
    paddingTop: TOPBAR_H,
    paddingLeft: isDesktop ? SIDEBAR_W + MAIN_GUTTER : 16,
    boxSizing: "border-box",
    position: "relative",
    minWidth: 0,
  }),

  headerWrap: {
    display: "flex",
    flexWrap: "wrap",
    gap: 12,
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 18,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" },
  titleWrap: {},
  title: { fontSize: 22, fontWeight: 900, letterSpacing: .2, color: C.ink },
  subtitle: { fontSize: 12, color: C.meta, marginTop: 2 },
  pill: {
    display: "inline-flex", alignItems: "center", gap: 8,
    background: "rgba(20,48,86,.9)",
    color: C.mute, border: `1px solid ${C.border}`,
    padding: "6px 10px", borderRadius: 999, fontSize: 12, fontWeight: 700
  },
  count: {
    display: "inline-flex", alignItems: "center",
    background: "#0f1931", color: C.mute,
    padding: "3px 8px", borderRadius: 999, fontSize: 12, marginLeft: 8
  },

  headerRight: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
  searchWrap: { position: "relative" },
  searchInput: {
    padding: "10px 12px 10px 36px",
    borderRadius: 10,
    border: `1px solid transparent`,
    background: C.panel2,
    color: C.ink, fontSize: 13, outline: "none",
  },
  searchIcon: { position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#93b5ff" },
  selectWrap: { position: "relative" },
  select: {
    appearance: "none",
    padding: "10px 34px 10px 10px",
    borderRadius: 10,
    border: `1px solid transparent`,
    background: C.panel2, color: C.ink, fontSize: 13, outline: "none",
  },
  selectIcon: { position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#93b5ff" },
  newBtn: {
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "10px 12px", borderRadius: 10,
    background: C.blue, color: C.bg, fontWeight: 800, textDecoration: "none",
  },

  section: {
    background: C.panel, borderRadius: 16, padding: 16,
    boxShadow: C.shadowM, border: `1px solid ${C.border}`,
  },
  sectionHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 },
  sectionTitle: { display: "flex", alignItems: "center", gap: 10 },
  sectionTitleIcon: {
    width: 28, height: 28, borderRadius: 8,
    display: "grid", placeItems: "center",
    background: "#0f1931", color: C.blue,
  },
  sectionTitleText: { fontSize: 15, fontWeight: 900, color: C.ink },

  // grids
  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: 16,
  },
  twoColMd: {
    display: "grid",
    gap: 16,
    gridTemplateColumns: "1fr 1fr",
  },

  approvedGrid: {
    display: "grid",
    gap: 16,
    gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
  },

  // tiles
  rowTile: {
    background: C.tile, border: `1px solid ${C.border}`, borderRadius: 14,
    padding: 12, boxShadow: C.shadowS,
  },
  rowTop: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 },
  rowTitle: { fontSize: 13, fontWeight: 700, color: C.ink, lineHeight: 1.2, margin: 0 },
  rowMeta: { display: "flex", alignItems: "center", gap: 8, marginTop: 6, fontSize: 11, color: C.meta },
  pillSmall: (bg, color) => ({
    padding: "2px 8px", borderRadius: 999, background: bg, color, fontSize: 11, fontWeight: 700
  }),
  openBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "6px 10px", fontSize: 11, borderRadius: 8,
    background: "#1f2a44", color: C.ink, textDecoration: "none", fontWeight: 700
  },
  rowBottom: { display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 10, fontSize: 11 },

  // small card (approved)
  smallCard: {
    background: C.tile, border: `1px solid ${C.border}`, borderRadius: 14,
    padding: 12, boxShadow: C.shadowS
  },

  // dropdown
  ddWrap: { position: "relative" },
  ddBtn: (bg, color) => ({
    display: "inline-flex", alignItems: "center", gap: 8,
    padding: "7px 10px", borderRadius: 8, fontSize: 12, fontWeight: 800,
    background: bg, color, border: "1px solid rgba(255,255,255,.08)", cursor: "pointer"
  }),
  ddMenu: {
    position: "absolute", right: 0, top: "calc(100% + 8px)",
    width: 180, borderRadius: 12, padding: 8, zIndex: 30,
    background: "#0f1931", border: `1px solid ${C.border}`, boxShadow: C.shadowM
  },
  ddItem: (bg, color) => ({
    width: "100%", textAlign: "left",
    display: "flex", alignItems: "center", gap: 10,
    padding: "9px 12px", borderRadius: 10, fontSize: 13,
    background: bg, color, fontWeight: 700, cursor: "pointer", marginBottom: 8
  }),

  // empty/skeleton
  empty: { background: C.tile, borderRadius: 12, padding: 20, display: "grid", placeItems: "center", color: C.mute, border: `1px solid ${C.border}` },
  skRow: { background: C.tile, borderRadius: 12, padding: 12, border: `1px solid ${C.border}` },
  skBar: (w=120) => ({ height: 10, width: w, background: "rgba(255,255,255,.08)", borderRadius: 8 }),
};

/* ------------------------------ Utilities -------------------------------- */

function readUser() {
  try {
    return JSON.parse(localStorage.getItem("user")) || {};
  } catch {
    return {};
  }
}

const toJsonSafe = async (res) => {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    console.error("Non-JSON response:", text.slice(0, 300));
    throw new Error("Invalid JSON from server");
  }
};

const toneKey = (s) => {
  const k = String(s || "new").toLowerCase();
  if (k === "approved") return "approved";
  if (k === "pending") return "pending";
  if (k === "rejected") return "rejected";
  return "new";
};

const shortDate = (s) => (s ? String(s).split(" ")[0] : "");

// status chip colors
const chipTone = {
  approved: { bg: C.greenSoft, color: "#b7f7df" },
  pending:  { bg: "rgba(99,102,241,.15)", color: "#c7cfff" },
  new:      { bg: C.skySoft, color: "#bfe6ff" },
  rejected: { bg: C.redSoft, color: "#ffc2d0" },
};

// dropdown button colors
const ddTone = {
  approved: { bg: C.green, color: "#fff" },
  pending:  { bg: C.green, color: "#fff" },
  new:      { bg: C.blueD, color: "#fff" },
  rejected: { bg: C.red,   color: "#fff" },
};

/* -------------------------------- Component ------------------------------- */

export default function ClubProjects() {
  // desktop check
  const useDesktop = () => {
    const [isDesktop, set] = React.useState(() => window.matchMedia("(min-width: 1024px)").matches);
    React.useEffect(() => {
      const mq = window.matchMedia("(min-width: 1024px)");
      const on = (e) => set(e.matches);
      mq.addEventListener?.("change", on) || mq.addListener(on);
      return () => mq.removeEventListener?.("change", on) || mq.removeListener(on);
    }, []);
    return isDesktop;
  };
  const isDesktop = useDesktop();

  /* ------------------------------ User/Admin ------------------------------ */
  const rawUser = useMemo(() => readUser(), []);
  const user = rawUser?.name ? rawUser : { name: "Unknown User", role: "Member", image: "/user.png" };
  const isAdmin =
    String(user?.role || "").toLowerCase().includes("admin") ||
    user?.is_admin === 1 ||
    user?.isAdmin === 1;

  /* --------------------------------- State -------------------------------- */
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [projects, setProjects] = useState([]);
  const [query, setQuery] = useState("");
  const [sortKey, setSortKey] = useState("created_desc");

  // Cache of { [SLIITID]: name }
  const [memberNames, setMemberNames] = useState({});
  const [namesLoading, setNamesLoading] = useState(false);

  /* --------------------------------- Load --------------------------------- */
  const loadProjects = useCallback(async () => {
    try {
      setErr("");
      setLoading(true);
      let data;

      if (isAdmin) {
        try {
          const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_projects_all.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({}),
          });
          if (!res.ok) throw new Error();
          const d = await toJsonSafe(res);
          if (d?.success && Array.isArray(d.projects)) data = d;
        } catch {}

        if (!data) {
          try {
            const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=list_all`);
            if (res.ok) {
              const d = await toJsonSafe(res);
              if (d?.success && Array.isArray(d.projects)) data = d;
            }
          } catch {}
        }

        if (!data) {
          const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_projects_visible.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ SLIITID: user?.SLIITID || null }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          data = await toJsonSafe(res);
        }
      } else {
        try {
          const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_projects_visible.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ SLIITID: user?.SLIITID || null }),
          });
          if (!res.ok) throw new Error();
          data = await toJsonSafe(res);
        } catch {
          const res = await fetch(`${ABSOLUTE_HOST}/Backend/get_projects_by_user.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ SLIITID: user?.SLIITID || null }),
          });
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          data = await toJsonSafe(res);
        }
      }

      setProjects(Array.isArray(data?.projects) ? data.projects : []);
    } catch {
      setErr("Failed to load projects");
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, user?.SLIITID]);

  useEffect(() => { loadProjects(); }, [loadProjects]);

 /* -------------------------- Fetch Member Names --------------------------- */
useEffect(() => {
  // unique, trimmed ids from projects
  const ids = Array.from(
    new Set(
      projects
        .map(p => String(p?.created_by_sliitid ?? '').trim())
        .filter(Boolean)
    )
  ).filter(id => !(id in memberNames));

  if (!ids.length) return;

  const abort = new AbortController();
  const { signal } = abort;

  (async () => {
    setNamesLoading(true);
    try {
      // Split into numeric SLIITLeoID vs string SLIITID
      const numericIds = ids.filter(id => /^\d+$/.test(id)).map(id => Number(id));
      const stringIds  = ids.filter(id => !/^\d+$/.test(id)); // likely ITxxxxxxx

      const merged = {};

      // 1) Bulk fetch by numeric SLIITLeoID (only if we actually have numeric ids)
      if (numericIds.length) {
        try {
          const bulkRes = await fetch(
            `${ABSOLUTE_HOST}/Backend/memberbasic_api.php?action=names_by_ids`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ ids: numericIds }),
              signal,
            }
          );

          if (bulkRes.ok) {
            const bulkData = await bulkRes.json();
            // backend returns: { success: true, data: [{ SLIITLeoID, FullName }, ...] }
            if (bulkData?.success && Array.isArray(bulkData.data)) {
              for (const row of bulkData.data) {
                const key = String(row?.SLIITLeoID ?? '').trim();
                const name = String(row?.FullName ?? '').trim();
                if (key && name) merged[key] = name;
              }
            }
          }
        } catch {
          // ignore bulk errors; we'll still try strings below
        }
      }

      // 2) Fetch by SLIITID (string) individually (concurrent, safe)
      if (stringIds.length) {
        const lookups = await Promise.allSettled(
          stringIds.map(async (id) => {
            const r = await fetch(
              `${ABSOLUTE_HOST}/Backend/memberbasic_api.php?action=get_by_sliitid`,
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                // IMPORTANT: backend expects "sliitid" (lowercase)
                body: JSON.stringify({ sliitid: id }),
                signal,
              }
            );
            if (!r.ok) return [id, null];
            const d = await r.json();
            // backend returns: { success: true, data: { FullName, SLIITID, ... } } or null
            const name =
              d?.data?.FullName ??
              d?.FullName ??
              d?.data?.name ??
              d?.name ??
              '';
            return [id, name ? String(name).trim() : null];
          })
        );

        for (const res of lookups) {
          if (res.status === 'fulfilled') {
            const [id, name] = res.value;
            if (id && name) merged[id] = name;
          }
        }
      }

      // 3) Commit any new names
      if (Object.keys(merged).length) {
        setMemberNames(cur => ({ ...cur, ...merged }));
      }
    } catch {
      // ignore (network/abort)
    } finally {
      if (!signal.aborted) setNamesLoading(false);
    }
  })();

  return () => abort.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [projects, memberNames]);


  /* ------------------------------ Name Resolve ----------------------------- */
  const creatorName = (p) => {
    if (p?.created_by_name && String(p.created_by_name).trim()) return p.created_by_name;
    const id = String(p?.created_by_sliitid || "").trim();
    if (id && memberNames[id]) return memberNames[id];
    return (
      p?.created_by ||
      p?.createdUser ||
      p?.created_user ||
      p?.creator ||
      p?.owner ||
      p?.proposed_by ||
      p?.submitted_by ||
      (id ? id : "Unknown")
    );
  };

  /* ------------------------------ Transform -------------------------------- */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = [...projects];

    if (q) {
      list = list.filter((p) => {
        const nameHit = String(p.name || "").toLowerCase().includes(q);
        const creatorHit = String(creatorName(p) || "").toLowerCase().includes(q);
        return nameHit || creatorHit;
      });
    }

    if (sortKey === "created_desc") {
      list.sort((a, b) => {
        const ad = new Date(a.created_at || 0).getTime();
        const bd = new Date(b.created_at || 0).getTime();
        return bd - ad;
      });
    } else if (sortKey === "name_asc") {
      list.sort((a, b) => String(a.name || "").localeCompare(b.name || ""));
    }

    return list;
  }, [projects, query, sortKey, memberNames]);

  const pending = filtered.filter((p) => toneKey(p.status) === "pending");
  const fresh   = filtered.filter((p) => toneKey(p.status) === "new");
  const approved= filtered.filter((p) => toneKey(p.status) === "approved");

  /* ---------------------------- Status Update ------------------------------ */
  const updateStatus = useCallback(
    async (project, next) => {
      if (!isAdmin) return;
      const id = Number(project.id);
      if (!id) return;

      const prev = [...projects];
      setProjects((cur) => cur.map((p) => (p.id === id ? { ...p, status: next } : p)));

      try {
        const res = await fetch(`${ABSOLUTE_HOST}/Backend/project_api.php?action=update_status`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id,
            status: next,
            approved_by: user?.name || "Admin",
          }),
        });
        const d = await toJsonSafe(res);
        if (!d?.success) {
          alert(d?.message || "Status update failed");
          setProjects(prev);
        }
      } catch {
        alert("Status update failed");
        setProjects(prev);
      }
    },
    [isAdmin, projects, user?.name]
  );

  /* ----------------------------------- UI ---------------------------------- */
  const HeaderBar = () => (
    <div style={S.headerWrap}>
      <div style={S.headerLeft}>
        <div style={S.titleWrap}>
          <div style={S.title}>Club Projects</div>
          <div style={S.subtitle}>Compact, high-performance tiles • navy design system</div>
        </div>

        {isAdmin && (
          <span style={S.pill} title="You are viewing admin-wide data">
            <FaShieldAlt /> Admin Mode
          </span>
        )}

        <span style={S.count}>{filtered.length} items</span>
      </div>

      <div style={S.headerRight}>
        {/* Search */}
        <div style={S.searchWrap}>
          <FaSearch style={S.searchIcon} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by project or creator…"
            style={S.searchInput}
          />
        </div>

        {/* Sort */}
        <div style={S.selectWrap}>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            style={S.select}
          >
            <option value="created_desc">Newest first</option>
            <option value="name_asc">Name A → Z</option>
          </select>
          <FaChevronDown style={S.selectIcon} />
        </div>

        {/* New */}
        <Link to="/create-project" style={S.newBtn}><FaPlus /> New</Link>
      </div>
    </div>
  );

  const Section = ({ title, Icon, count, children }) => (
    <section style={S.section}>
      <header style={S.sectionHeader}>
        <div style={S.sectionTitle}>
          <span style={S.sectionTitleIcon}><Icon size={12} /></span>
          <h2 style={S.sectionTitleText}>{title}</h2>
        </div>
        <span style={{ ...S.count, marginLeft: 0 }}>{count ?? 0}</span>
      </header>
      {children}
    </section>
  );

  // Row tile (Pending/New)
  const RowTile = ({ project }) => {
    const k = toneKey(project.status);
    const { bg, color } = chipTone[k];

    return (
      <div style={S.rowTile}>
        <div style={S.rowTop}>
          <div style={{ minWidth: 0 }}>
            <h3 style={S.rowTitle} title={project.name}>{project.name}</h3>
            <div style={S.rowMeta}>
              <span style={S.pillSmall("rgba(2,132,199,.16)", "#bfe6ff")}>{creatorName(project)}</span>
              {project.created_at && (<span>{shortDate(project.created_at)}</span>)}
            </div>
            <div style={{ marginTop: 8 }}>
              <Link to={`/project/${project.id}`} style={S.openBtn}>Open</Link>
            </div>
          </div>

          {isAdmin && (
            <StatusDropdown
              current={k}
              onSelect={(s) => updateStatus(project, s)}
            />
          )}
        </div>

        <div style={S.rowBottom}>
          <span style={S.pillSmall(bg, color)} className="capitalize">{k}</span>
          <span style={{ color: C.gold, maxWidth: "60%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {project.approved_by ? `by ${project.approved_by}` : ""}
          </span>
        </div>
      </div>
    );
  };

  // Small card (Approved)
  const SmallCard = ({ project }) => {
    const k = toneKey(project.status);
    const { bg, color } = chipTone[k];

    return (
      <div style={S.smallCard}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
          <div style={{ minWidth: 0 }}>
            <h3 style={S.rowTitle} title={project.name}>{project.name}</h3>
            <div style={S.rowMeta}>
              <span style={S.pillSmall("rgba(2,132,199,.16)", "#bfe6ff")}>{creatorName(project)}</span>
              {project.created_at && (<span>{shortDate(project.created_at)}</span>)}
            </div>
          </div>

          {isAdmin && (
            <StatusDropdown
              compact
              current={k}
              onSelect={(s) => updateStatus(project, s)}
            />
          )}
        </div>

        <div style={S.rowBottom}>
          <span style={S.pillSmall(bg, color)} className="capitalize">{k}</span>
          <span style={{ color: C.gold, maxWidth: "60%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {project.approved_by ? `by ${project.approved_by}` : ""}
          </span>
        </div>

        <div style={{ marginTop: 8 }}>
          <Link to={`/project/${project.id}`} style={S.openBtn}>Open</Link>
        </div>
      </div>
    );
  };

  // Dropdown
  function StatusDropdown({ current, onSelect, compact }) {
    const [open, setOpen] = useState(false);
    const { bg, color } = ddTone[current] || ddTone.new;

    const Item = ({ label, icon, tone, value }) => {
      const t = ddTone[tone];
      return (
        <button
          onClick={() => { onSelect(value); setOpen(false); }}
          style={S.ddItem(t.bg, "#fff")}
        >
          <span style={{ fontSize: 12 }}>{icon}</span>
          <span>{label}</span>
        </button>
      );
    };

    return (
      <div style={S.ddWrap}>
        <button
          onClick={() => setOpen((o) => !o)}
          style={S.ddBtn(bg, color)}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          {current === "approved" ? "Approved" :
           current === "pending"  ? "Pending"  :
           current === "rejected" ? "Rejected" : "New"}
          <FaChevronDown style={{ opacity: .9 }} />
        </button>

        {open && (
          <div style={S.ddMenu} onMouseLeave={() => setOpen(false)} role="menu">
            <Item label="Approve" icon={<FaThumbsUp />} tone="approved" value="approved" />
            <Item label="Pending" icon={<FaClock />} tone="pending" value="pending" />
            <Item label="New" icon={<FaStar />} tone="new" value="new" />
            <Item label="Reject" icon={<FaTimesCircle />} tone="rejected" value="rejected" />
          </div>
        )}
      </div>
    );
  }

  // Empty + Skeleton
  const Empty = ({ text, tone = "new" }) => {
    const Icon =
      tone === "approved" ? FaCheckCircle :
      tone === "pending"  ? FaHourglassHalf :
      tone === "rejected" ? FaExclamationTriangle : FaRocket;

    return (
      <div style={S.empty}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <Icon style={{ opacity: .9 }} /> <span>{text}</span>
        </div>
      </div>
    );
  };

  const RowSkeleton = () => (
    <div style={S.skRow} aria-hidden>
      <div style={{ ...S.skBar(150), marginBottom: 10 }} />
      <div style={S.skBar(260)} />
    </div>
  );

  const SkeletonCard = () => (
    <div style={S.smallCard} aria-hidden>
      <div style={{ ...S.skBar(140), marginBottom: 10 }} />
      <div style={{ ...S.skBar(100), marginBottom: 10 }} />
      <div style={S.skBar(220)} />
    </div>
  );

  /* --------------------------------- Render -------------------------------- */
  return (
    <div style={{ ...S.app, ...S.font }}>
      <Sidebar />
      <Topbar user={user} />

      <main style={S.main(isDesktop)}>
        {/* Header */}
        <HeaderBar />

        {/* Pending + New */}
        <div style={isDesktop ? S.twoColMd : S.twoCol}>
          <Section title="Pending" Icon={FaHourglassHalf} count={pending.length}>
            {loading || namesLoading ? (
              <div style={{ display: "grid", gap: 12 }}>
                {Array.from({ length: 4 }).map((_, i) => <RowSkeleton key={i} />)}
              </div>
            ) : !pending.length ? (
              <Empty text="Nothing pending." tone="pending" />
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {pending.map((p) => <RowTile key={p.id} project={p} />)}
              </div>
            )}
          </Section>

          <Section title="New" Icon={FaRocket} count={fresh.length}>
            {loading || namesLoading ? (
              <div style={{ display: "grid", gap: 12 }}>
                {Array.from({ length: 4 }).map((_, i) => <RowSkeleton key={i} />)}
              </div>
            ) : !fresh.length ? (
              <Empty text="No drafts yet." tone="new" />
            ) : (
              <div style={{ display: "grid", gap: 12 }}>
                {fresh.map((p) => <RowTile key={p.id} project={p} />)}
              </div>
            )}
          </Section>
        </div>

        {/* Approved */}
<div style={{ marginTop: 16 }}>
  <Section title="Approved" Icon={FaCheckCircle} count={approved.length}>
    {loading || namesLoading ? (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: 12,
          padding: "6px 6px 2px",
        }}
      >
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonCard key={i} />
        ))}
      </div>
    ) : !approved.length ? (
      <div
        style={{
          padding: "10px 12px",
          borderRadius: 12,
          background: "rgba(147,197,253,.06)",
          border: "1px dashed rgba(147,197,253,.16)",
          color: "#cfe0ff",
          textAlign: "center",
          fontSize: 13,
        }}
      >
        No approved projects yet.
      </div>
    ) : (
      <div
        /* Compact 3-up grid, collapses down gracefully */
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 12,
          padding: "6px 6px 2px",
        }}
      >
        {approved.map((p) => (
          <div
            key={p.id}
            /* Slim card wrapper to soften “box in box” feel */
            style={{
              borderRadius: 12,
              background: "rgba(2,8,23,.32)",         // softer than panel bg
              border: "1px solid rgba(147,197,253,.10)",
              boxShadow: "0 6px 16px rgba(0,0,0,.20)",
              padding: 10,
              minHeight: 86,
              display: "flex",
              flexDirection: "column",
              gap: 8,
            }}
          >
            <SmallCard project={p} />
          </div>
        ))}
      </div>
    )}
  </Section>
</div>


        {err && (
          <div style={{
            marginTop: 14, borderRadius: 12, padding: "12px 14px",
            background: "#3b1e2a", color: "#ffb4c4"
          }}>
            {err}
          </div>
        )}
      </main>
    </div>
  );
}
